import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createChart, Time, AreaSeries, CandlestickSeries, LineSeries, HistogramSeries, createSeriesMarkers } from 'lightweight-charts';
import { CandlestickChart, ChevronsRight } from 'lucide-react';
import ChartToolbarButton from './ChartToolbarButton';
import { strategySeriesColors, themeColors } from '../../styles/theme';
import { prepareChartData, toDayString } from './lightweightChartData';
import { buildThemedChartOptions } from './chartTheme';

const chartRightGutterPx = 56;
const EMPTY_CHART_ARRAY: never[] = [];

interface ChartProps {
    data: any[];
    trades?: any[];
    volumeData?: ChartVolumePoint[];
    lineOverlays?: ChartLineOverlay[];
    customMarkers?: ChartMarker[];
    lowerPaneData?: any[];
    type: 'equity' | 'symbol';
    symbolStyle?: 'candles' | 'line';
    timeVisible?: boolean;
    initialVisibleBars?: number;
    latestVisibleBars?: number;
    showMa30?: boolean;
    showMa200?: boolean;
    showBuyMarkers?: boolean;
    showCloseMarkers?: boolean;
    equityVariant?: 'area' | 'line';
    height?: number;
    range?: RangeOption;
    resolution?: 'native' | '1D';
    onRangeChange?: (range: RangeOption) => void;
    resetViewToken?: number;
    hideControls?: boolean;
    showLatestControl?: boolean;
    oosStartDate?: string;
    lineColor?: string;
    topColor?: string;
    bottomColor?: string;
    candleUpColor?: string;
    candleDownColor?: string;
    lowerPaneLabel?: string;
    lowerPaneColor?: string;
    lowerPaneValueSuffix?: string;
    lowerPaneValuePrefix?: string;
    visibleSeriesKeys?: string[];
    displayMode?: 'dollar' | 'percent' | 'gross';
    showMainSeries?: boolean;
    compareGross?: boolean;
    showGrossSeries?: boolean;
    lowerPaneType?: 'line' | 'histogram';
    showEquityCandles?: boolean;
}

export interface ChartVolumePoint {
    date: string;
    value: number;
    color?: string;
}

export interface ChartLineOverlay {
    title?: string;
    color: string;
    lineWidth?: 1 | 2 | 3 | 4;
    data: Array<{ date: string; value: number }>;
}

export interface ChartMarker {
    date: string;
    position: 'aboveBar' | 'belowBar' | 'inBar';
    color: string;
    shape: 'circle' | 'square' | 'arrowUp' | 'arrowDown';
    text?: string;
    size?: number;
}

export type RangeOption = '1D' | '3D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'YTD' | 'ALL';

export const LightweightChart: React.FC<ChartProps> = ({
    data,
    trades = EMPTY_CHART_ARRAY,
    volumeData = EMPTY_CHART_ARRAY,
    lineOverlays = EMPTY_CHART_ARRAY,
    customMarkers = EMPTY_CHART_ARRAY,
    lowerPaneData = EMPTY_CHART_ARRAY,
    type,
    symbolStyle = 'candles',
    timeVisible,
    initialVisibleBars,
    latestVisibleBars,
    showMa30 = false,
    showMa200 = false,
    showBuyMarkers = true,
    showCloseMarkers = true,
    equityVariant = 'area',
    height = 400,
    range,
    resetViewToken = 0,
    resolution = 'native',
    onRangeChange,
    hideControls = false,
    showLatestControl = false,
    oosStartDate,
    lineColor = themeColors.neon,
    topColor = themeColors.neonArea,
    bottomColor = themeColors.neonTransparent,
    candleUpColor = themeColors.success,
    candleDownColor = themeColors.danger,
    lowerPaneLabel = 'Lower',
    lowerPaneColor = themeColors.dangerLight,
    lowerPaneValueSuffix = '',
    lowerPaneValuePrefix = '',
    visibleSeriesKeys,
    displayMode = 'dollar',
    showMainSeries = true,
    compareGross = false,
    showGrossSeries = true,
    lowerPaneType = 'line',
    showEquityCandles = false,
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<any>(null);
    const seriesRef = useRef<any>(null);
    const overlaySeriesRef = useRef<any[]>([]);
    const processedDataRef = useRef<any[]>(data);
    const resizeObserver = useRef<ResizeObserver | null>(null);
    const visibleLogicalRangeRef = useRef<any>(null);
    const appliedRangeRef = useRef<RangeOption | null>(null);
    const appliedResetViewTokenRef = useRef(resetViewToken);

    const [internalRange, setInternalRange] = useState<RangeOption>('ALL');
    const [hoverPoint, setHoverPoint] = useState<any | null>(null);
    const [lowerHoverPoint, setLowerHoverPoint] = useState<any | null>(null);
    const selectedRange = range || internalRange;

    // Stable strategy keys for consistent coloring
    const allStrategyKeys = useMemo(() => {
        if (!data || data.length === 0) return [];
        const keys = new Set<string>();
        data.forEach(d => {
            Object.keys(d).forEach(k => {
                if (k.startsWith('equity_') && k !== 'equity_curve') {
                    keys.add(k);
                }
            });
        });
        return Array.from(keys).sort(); // Sort to ensure stable order
    }, [data]);

    const handleRangeChange = (r: RangeOption) => {
        if (onRangeChange) {
            onRangeChange(r);
        } else {
            setInternalRange(r);
        }
    };

    const processTime = useCallback((dateStr: string): Time => {
        if (resolution === '1D') return toDayString(dateStr) as Time;

        if (dateStr.includes(':')) {
            const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
            if (match) {
                const [, year, month, day, hour, min, sec] = match;
                const fakeUtc = Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(min), parseInt(sec));
                return (fakeUtc / 1000) as Time;
            }
            const safeStr = dateStr.replace(' ', 'T');
            return (new Date(safeStr).getTime() / 1000) as Time;
        }
        return dateStr as Time;
    }, [resolution]);

    const updateOosOverlay = useCallback(() => {
        if (!overlayRef.current || !chartRef.current || !chartContainerRef.current || !oosStartDate) {
            return;
        }

        const overlay = overlayRef.current;
        const chart = chartRef.current;
        const timeScale = chart.timeScale();
        const width = chartContainerRef.current.clientWidth;
        const rightAxisInset = 64;
        const bottomAxisInset = 22;
        const minOverlayWidth = 36;
        const rangeData = processedDataRef.current || [];

        const toComparableMs = (dateStr: string): number => {
            if (resolution === '1D') {
                return new Date(`${toDayString(dateStr)}T12:00:00`).getTime();
            }
            const safeStr = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
            return new Date(safeStr).getTime();
        };

        const targetMs = toComparableMs(oosStartDate);
        const anchorPoint = rangeData.find((d) => toComparableMs(String(d.date)) >= targetMs) || rangeData[rangeData.length - 1];
        const x = anchorPoint ? timeScale.timeToCoordinate(processTime(String(anchorPoint.date))) : null;

        if (x == null || Number.isNaN(x)) {
            overlay.style.display = 'none';
            return;
        }

        const left = Math.max(0, Math.min(width, x));
        const usableRightEdge = Math.max(0, width - rightAxisInset);
        const overlayLeft = Math.min(left, Math.max(0, usableRightEdge - minOverlayWidth));
        const overlayWidth = Math.max(minOverlayWidth, usableRightEdge - overlayLeft);

        overlay.style.display = 'block';
        overlay.style.left = `${overlayLeft}px`;
        overlay.style.width = `${Math.max(0, overlayWidth)}px`;
        overlay.style.bottom = `${bottomAxisInset}px`;
    }, [oosStartDate, processTime, resolution]);

    // Helper to apply the visible range
    const applyRangeLogic = useCallback((showLatest = false) => {
        const rangeData = processedDataRef.current;
        if (!chartRef.current || !seriesRef.current || rangeData.length === 0) return;

        const chart = chartRef.current;
        const timeScale = chart.timeScale();

        if (selectedRange === 'ALL') {
            const visibleBars = showLatest ? latestVisibleBars : initialVisibleBars;
            if (visibleBars && visibleBars > 0) {
                timeScale.setVisibleLogicalRange({
                    from: Math.max(0, rangeData.length - visibleBars),
                    to: rangeData.length,
                });
            } else {
                timeScale.fitContent();
            }
            return;
        }

        const daysMap: Record<RangeOption, number> = {
            '1D': 1, '3D': 3, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, 'YTD': 0, 'ALL': 0
        };

        const lastItem = rangeData[rangeData.length - 1];
        const lastDate = new Date(lastItem.date.includes('T') ? lastItem.date : lastItem.date + 'T12:00:00');

        let targetStartDate: Date;
        if (selectedRange === 'YTD') {
            targetStartDate = new Date(new Date().getFullYear(), 0, 1);
        } else {
            targetStartDate = new Date(lastDate);
            targetStartDate.setDate(targetStartDate.getDate() - (daysMap[selectedRange] || 0));
        }

        const startIndex = rangeData.findIndex(d => {
            const itemDate = new Date(d.date.includes('T') ? d.date : d.date + 'T12:00:00');
            return itemDate >= targetStartDate;
        });

        timeScale.setVisibleLogicalRange({
            from: startIndex === -1 ? 0 : startIndex,
            to: rangeData.length
        });
        window.requestAnimationFrame(() => {
            if (chartRef.current !== chart) return;
            const barSpacing = timeScale.options().barSpacing || 6;
            timeScale.scrollToPosition(chartRightGutterPx / barSpacing, false);
        });
    }, [selectedRange, initialVisibleBars, latestVisibleBars]);

    const formatHoverDate = (dateStr: string) => {
        const safeStr = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
        const dt = new Date(safeStr);
        if (Number.isNaN(dt.getTime())) return dateStr;
        if (resolution === '1D') {
            return dt.toLocaleDateString();
        }
        return dt.toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Main Chart Creation and Series Update Effect
    useEffect(() => {
        if (!chartContainerRef.current) return;

        const chart = createChart(chartContainerRef.current, buildThemedChartOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight || height,
            timeVisible: timeVisible ?? resolution === 'native',
            rightOffsetPixels: chartRightGutterPx,
            includePaneSeparators: true,
        }));

        chartRef.current = chart;

        let series: any;
        const overlaySeries: any[] = [];
        const { data: processedData, lowerPaneData: processedLowerPaneData } = prepareChartData({
            data,
            lowerPaneData,
            type,
            resolution,
            showEquityCandles,
            displayMode,
        });

        processedDataRef.current = processedData;

        if (type === 'equity') {
            if (equityVariant === 'line') {
                series = chart.addSeries(LineSeries, {
                    color: lineColor,
                    lineWidth: 2,
                });
            } else {
                series = chart.addSeries(AreaSeries, {
                    lineColor,
                    topColor,
                    bottomColor,
                    lineWidth: 2,
                });
            }

            if (displayMode === 'percent') {
                series.applyOptions({
                    priceFormat: {
                        type: 'custom',
                        formatter: (price: number) => `${price >= 0 ? '+' : ''}${price.toFixed(2)}%`,
                    },
                });
            }

            if (showMainSeries) {
                series.setData(processedData.map(d => {
                    let val = d.equity;
                    if (displayMode === 'gross') val = d.equity_gross ?? d.equity;
                    return { time: processTime(d.date), value: Number(val || 0) };
                }));
            } else {
                series.setData([]);
            }

            // Render individual strategy equity curves
            if (allStrategyKeys.length > 0) {
                allStrategyKeys.forEach((key, idx) => {
                    if (visibleSeriesKeys && !visibleSeriesKeys.includes(key)) return;

                    const stratSeries = chart.addSeries(LineSeries, {
                        color: strategySeriesColors[idx % strategySeriesColors.length],
                        lineWidth: 1,
                        priceLineVisible: false,
                        lastValueVisible: false,
                    });

                    if (displayMode === 'percent') {
                        stratSeries.applyOptions({
                            priceFormat: {
                                type: 'custom',
                                formatter: (price: number) => `${price >= 0 ? '+' : ''}${price.toFixed(2)}%`,
                            },
                        });
                    }

                    stratSeries.setData(processedData.map(d => ({ time: processTime(d.date), value: d[key] })));
                    overlaySeries.push(stratSeries);
                });
            }

            // Render Gross Comparison line if requested
            if (compareGross && showGrossSeries && processedData.some(d => d.equity_gross !== undefined)) {
                const grossSeries = chart.addSeries(LineSeries, {
                    color: themeColors.warningBright,
                    lineWidth: 1,
                    lineStyle: 2, // LargeDashed
                    priceLineVisible: false,
                    lastValueVisible: false,
                });

                grossSeries.setData(processedData.map(d => {
                    const isGrossMode = displayMode === 'gross';
                    const targetVal = isGrossMode ? d.equity : d.equity_gross;
                    return {
                        time: processTime(d.date),
                        value: targetVal
                    };
                }));
                overlaySeries.push(grossSeries);
            }
        } else {
            if (processedData.length > 0 && 'open' in processedData[0]) {
                if (symbolStyle === 'line') {
                    series = chart.addSeries(LineSeries, {
                        color: lineColor,
                        lineWidth: 2,
                        crosshairMarkerVisible: true,
                        priceLineVisible: true,
                        lastValueVisible: true,
                    });
                    series.setData(processedData.map(d => ({ time: processTime(d.date), value: d.close })));
                } else {
                    series = chart.addSeries(CandlestickSeries, {
                        upColor: candleUpColor,
                        downColor: candleDownColor,
                        borderVisible: false,
                        wickUpColor: candleUpColor,
                        wickDownColor: candleDownColor,
                        priceLineVisible: true,
                        lastValueVisible: true,
                    });
                    series.setData(processedData.map(d => ({
                        time: processTime(d.date), open: d.open, high: d.high, low: d.low, close: d.close
                    })));
                }
            } else {
                series = chart.addSeries(LineSeries, { color: themeColors.infoDark, lineWidth: 2 });
                series.setData(processedData.map(d => ({ time: processTime(d.date), value: d.price || d.equity })));
            }
        }

        seriesRef.current = series;

        if (type === 'symbol') {
            if (showMa30 && processedData.some((d) => d.ma30 != null)) {
                const ma30Series = chart.addSeries(LineSeries, {
                    color: themeColors.info,
                    lineWidth: 1,
                    priceLineVisible: false,
                    lastValueVisible: false,
                });
                ma30Series.setData(
                    processedData
                        .filter((d) => d.ma30 != null)
                        .map((d) => ({ time: processTime(d.date), value: d.ma30 }))
                );
                overlaySeries.push(ma30Series);
            }
            if (showMa200 && processedData.some((d) => d.ma200 != null)) {
                const ma200Series = chart.addSeries(LineSeries, {
                    color: themeColors.warning,
                    lineWidth: 1,
                    priceLineVisible: false,
                    lastValueVisible: false,
                });
                ma200Series.setData(
                    processedData
                        .filter((d) => d.ma200 != null)
                        .map((d) => ({ time: processTime(d.date), value: d.ma200 }))
                );
                overlaySeries.push(ma200Series);
            }
        }

        lineOverlays.forEach((overlay) => {
            if (overlay.data.length === 0) return;
            const overlaySeriesItem = chart.addSeries(LineSeries, {
                color: overlay.color,
                lineWidth: overlay.lineWidth ?? 2,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: false,
                title: overlay.title ?? '',
            });
            overlaySeriesItem.setData(overlay.data.map((point) => ({
                time: processTime(point.date),
                value: point.value,
            })));
            overlaySeries.push(overlaySeriesItem);
        });

        if (volumeData.length > 0) {
            const volumeSeries = chart.addSeries(HistogramSeries, {
                priceFormat: { type: 'volume' },
                priceScaleId: '',
                lastValueVisible: false,
                priceLineVisible: false,
            });
            volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
            volumeSeries.setData(volumeData.map((point) => ({
                time: processTime(point.date),
                value: point.value,
                ...(point.color ? { color: point.color } : {}),
            })));
            overlaySeries.push(volumeSeries);
        }

        if (processedLowerPaneData.length > 0) {
            let lowerSeries;
            const lowerPanePriceFormat = (lowerPaneValuePrefix || lowerPaneValueSuffix)
                ? {
                    type: 'custom' as const,
                    formatter: (price: number) => `${lowerPaneValuePrefix}${lowerPaneValueSuffix === '%' && price > 0 ? '+' : ''}${price.toFixed(2)}${lowerPaneValueSuffix}`,
                }
                : undefined;
            if (lowerPaneType === 'histogram') {
                lowerSeries = chart.addSeries(HistogramSeries, {
                    color: lowerPaneColor,
                    priceLineVisible: false,
                    priceFormat: lowerPanePriceFormat,
                }, 1);
                lowerSeries.setData(
                    processedLowerPaneData.map((point) => {
                        const val = point.equity;
                        return {
                            time: processTime(point.date),
                            value: val,
                            color: val >= 0 ? themeColors.success : themeColors.danger
                        };
                    })
                );
            } else {
                lowerSeries = chart.addSeries(LineSeries, {
                    color: lowerPaneColor,
                    lineWidth: 2,
                    priceLineVisible: false,
                    priceFormat: lowerPanePriceFormat,
                }, 1);
                lowerSeries.setData(
                    processedLowerPaneData.map((point) => ({
                        time: processTime(point.date),
                        value: point.equity,
                    }))
                );
            }
            overlaySeries.push(lowerSeries);

            const panes = chart.panes();
            if (panes[0]) panes[0].setStretchFactor(3);
            if (panes[1]) panes[1].setStretchFactor(1);
        }
        overlaySeriesRef.current = overlaySeries;

        // Add Markers
        const markers: any[] = [];
        if ((type === 'symbol' || type === 'equity') && Array.isArray(trades)) {
            const chartDataTimes = processedData.map(d => processTime(d.date));
            const snapToClosest = (targetTime: number | string): Time | null => {
                if (chartDataTimes.length === 0) return null;
                if (resolution === '1D') {
                    const tStr = typeof targetTime === 'number'
                        ? new Date(targetTime * 1000).toISOString().split('T')[0]
                        : toDayString(String(targetTime));
                    const exact = chartDataTimes.find((time) => String(time) === tStr);
                    if (exact !== undefined) return exact;
                    return chartDataTimes.find((time) => String(time) >= tStr) || chartDataTimes[chartDataTimes.length - 1];
                }
                const numericTarget = typeof targetTime === 'number'
                    ? targetTime
                    : Number(processTime(String(targetTime)));
                if (!Number.isFinite(numericTarget)) {
                    const exact = chartDataTimes.find((time) => String(time) === String(targetTime));
                    return exact ?? null;
                }
                let closest = chartDataTimes[0] as number;
                let minDiff = Math.abs(numericTarget - closest);
                for (let i = 1; i < chartDataTimes.length; i++) {
                    const current = chartDataTimes[i] as number;
                    const diff = Math.abs(numericTarget - current);
                    if (diff < minDiff) { minDiff = diff; closest = current; }
                }
                return closest as Time;
            };

            trades.forEach(t => {
                const entryTimeRaw = processTime(t.entry_date);
                const exitTimeRaw = t.status !== 'OPEN' && t.exit_date ? processTime(t.exit_date) : null;
                const entryTime = snapToClosest(entryTimeRaw as any);
                const exitTime = exitTimeRaw ? snapToClosest(exitTimeRaw as any) : null;

                if (entryTime && showBuyMarkers) {
                    const isLong = t.side === 'LONG';
                    markers.push({ time: entryTime, position: isLong ? 'belowBar' : 'aboveBar', color: isLong ? themeColors.successMarker : themeColors.dangerMarker, shape: isLong ? 'arrowUp' : 'arrowDown', text: isLong ? 'B' : 'S' });
                }
                if (exitTime && showCloseMarkers) {
                    const isLong = t.side === 'LONG';
                    const exitColor = t.status === 'STOP_LOSS' ? themeColors.warningMarker : (isLong ? themeColors.dangerMarker : themeColors.successMarker);
                    markers.push({ time: exitTime, position: isLong ? 'aboveBar' : 'belowBar', color: exitColor, shape: isLong ? 'arrowDown' : 'arrowUp', text: t.status === 'STOP_LOSS' ? 'SL' : 'C' });
                }
            });
        }

        if (oosStartDate) {
            markers.push({ time: processTime(oosStartDate), position: 'aboveBar', color: themeColors.warning, shape: 'arrowDown', text: 'OOS', size: 2 });
        }

        customMarkers.forEach((marker) => {
            markers.push({
                time: processTime(marker.date),
                position: marker.position,
                color: marker.color,
                shape: marker.shape,
                ...(marker.text ? { text: marker.text } : {}),
                ...(marker.size ? { size: marker.size } : {}),
            });
        });

        if (markers.length > 0) {
            createSeriesMarkers(series, markers.sort((a, b) => String(a.time).localeCompare(String(b.time))));
        }

        const savedVisibleRange = visibleLogicalRangeRef.current;
        if (savedVisibleRange) {
            chart.timeScale().setVisibleLogicalRange(savedVisibleRange);
        } else {
            applyRangeLogic();
            appliedRangeRef.current = selectedRange;
        }
        updateOosOverlay();
        const timeScale = chart.timeScale();
        const handleVisibleLogicalRangeChange = () => {
            visibleLogicalRangeRef.current = timeScale.getVisibleLogicalRange();
            updateOosOverlay();
        };
        const handleCrosshairMove = (param: any) => {
            if (!param?.time || !processedDataRef.current.length) {
                setHoverPoint(null);
                setLowerHoverPoint(null);
                return;
            }

            const targetTime = String(param.time);
            const matchedPoint = processedDataRef.current.find((point) => String(processTime(String(point.date))) === targetTime);
            const matchedLowerPoint = processedLowerPaneData.find((point) => String(processTime(String(point.date))) === targetTime);
            setHoverPoint(matchedPoint || null);
            setLowerHoverPoint(matchedLowerPoint || null);
        };
        timeScale.subscribeVisibleTimeRangeChange(updateOosOverlay);
        timeScale.subscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
        chart.subscribeCrosshairMove(handleCrosshairMove);

        resizeObserver.current = new ResizeObserver(entries => {
            const { width, height: h } = entries[0].contentRect;
            chart.applyOptions({ width, height: h });
            updateOosOverlay();
        });
        resizeObserver.current.observe(chartContainerRef.current);

        return () => {
            visibleLogicalRangeRef.current = timeScale.getVisibleLogicalRange();
            timeScale.unsubscribeVisibleTimeRangeChange(updateOosOverlay);
            timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
            chart.unsubscribeCrosshairMove(handleCrosshairMove);
            resizeObserver.current?.disconnect();
            chart.remove();
            chartRef.current = null;
            seriesRef.current = null;
            overlaySeriesRef.current = [];
            processedDataRef.current = [];
            setHoverPoint(null);
            setLowerHoverPoint(null);
        };
        // Range changes are applied below without rebuilding the chart instance.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, trades, volumeData, lineOverlays, customMarkers, lowerPaneData, type, symbolStyle, showMa30, showMa200, showBuyMarkers, showCloseMarkers, equityVariant, height, resolution, timeVisible, oosStartDate, lineColor, topColor, bottomColor, candleUpColor, candleDownColor, lowerPaneColor, lowerPaneValuePrefix, lowerPaneValueSuffix, visibleSeriesKeys, displayMode, showMainSeries, compareGross, showGrossSeries, lowerPaneType, showEquityCandles, allStrategyKeys, processTime, updateOosOverlay]);

    // Only toolbar range changes and explicit reset requests should move the
    // user's viewport. Data refreshes and crosshair hover rerenders preserve it.
    useEffect(() => {
        if (!chartRef.current || !seriesRef.current || data.length === 0) return;

        const resetRequested = appliedResetViewTokenRef.current !== resetViewToken;
        const rangeChanged = appliedRangeRef.current !== selectedRange;
        if (!resetRequested && !rangeChanged) return;

        if (resetRequested) visibleLogicalRangeRef.current = null;
        appliedResetViewTokenRef.current = resetViewToken;
        appliedRangeRef.current = selectedRange;
        applyRangeLogic();
        updateOosOverlay();
    }, [selectedRange, resetViewToken, applyRangeLogic, updateOosOverlay]);

    const rangeOptions: RangeOption[] = ['1D', '3D', '1W', '1M', '3M', '6M', 'YTD', '1Y', 'ALL'];

    return (
        <div className="relative w-full h-full">
            {(showLatestControl || !hideControls) && (
                <div className="absolute bottom-9 right-20 z-20">
                    <ChartToolbarButton
                        size="compact"
                        iconOnly
                        label="Scroll chart to latest"
                        title="Scroll chart to latest"
                        icon={<ChevronsRight className="h-4 w-4" />}
                        onClick={() => {
                            visibleLogicalRangeRef.current = null;
                            applyRangeLogic(true);
                            updateOosOverlay();
                        }}
                    />
                </div>
            )}
            {!hideControls && (
                <div className="absolute top-2 right-2 z-10 flex flex-wrap justify-end gap-1">
                    {type === 'symbol' && (
                        <div className="mr-1 flex rounded-sm border border-line bg-surface-2 p-0.5">
                            <span className="flex h-7 items-center gap-1.5 rounded-sm px-2 text-[10px] font-bold uppercase tracking-wider text-neon" title="Candlestick chart">
                                <CandlestickChart className="h-3.5 w-3.5" />
                                Candle
                            </span>
                        </div>
                    )}
                    {rangeOptions.map(r => (
                        <ChartToolbarButton
                            key={r}
                            size="compact"
                            label={r}
                            selected={selectedRange === r}
                            onClick={() => handleRangeChange(r)}
                        >
                            {r}
                        </ChartToolbarButton>
                    ))}
                </div>
            )}
            <div ref={chartContainerRef} className="w-full h-full bg-surface-3" />
            {hoverPoint && (
                <div className="pointer-events-none absolute bottom-10 left-3 z-9 rounded-lg border border-line bg-surface/90 px-3 py-2 text-[11px] text-white shadow-lg backdrop-blur-xs">
                    <div className="mb-1 font-mono text-white/80">
                        {formatHoverDate(String(hoverPoint.date))}
                    </div>
                    {type === 'symbol' ? (
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
                            <span className="text-white/60">O</span>
                            <span>{Number(hoverPoint.open ?? 0).toFixed(2)}</span>
                            <span className="text-white/60">H</span>
                            <span>{Number(hoverPoint.high ?? 0).toFixed(2)}</span>
                            <span className="text-white/60">L</span>
                            <span>{Number(hoverPoint.low ?? 0).toFixed(2)}</span>
                            <span className="text-white/60">C</span>
                            <span>{Number(hoverPoint.close ?? 0).toFixed(2)}</span>
                            {hoverPoint.ma30 != null && (
                                <>
                                    <span className="text-sky-300">MA30</span>
                                    <span>{Number(hoverPoint.ma30).toFixed(2)}</span>
                                </>
                            )}
                            {hoverPoint.ma200 != null && (
                                <>
                                    <span className="text-warning-500">MA200</span>
                                    <span>{Number(hoverPoint.ma200).toFixed(2)}</span>
                                </>
                            )}
                            {lowerHoverPoint && (
                                <>
                                    <span className={lowerPaneLabel === 'Daily P/L' && Number(lowerHoverPoint.equity ?? 0) >= 0 ? 'text-success-500' : 'text-error-500'}>{lowerPaneLabel}</span>
                                    <span className={lowerPaneLabel === 'Daily P/L' && Number(lowerHoverPoint.equity ?? 0) >= 0 ? 'text-success-500' : 'text-error-500'}>{lowerPaneValuePrefix}{Number(lowerHoverPoint.equity ?? 0).toFixed(2)}{lowerPaneValueSuffix}</span>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
                            <span className="text-white/60">{displayMode === 'percent' ? 'Return' : 'Total Equity'}</span>
                            <span className={displayMode === 'percent' ? (hoverPoint.equity >= 0 ? 'text-neon' : 'text-error-500') : 'text-white'}>
                                {displayMode === 'percent' ? (hoverPoint.equity >= 0 ? '+' : '') : '$'}
                                {Number(hoverPoint.equity ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                {displayMode === 'percent' ? '%' : ''}
                            </span>
                            {compareGross && showGrossSeries && hoverPoint.equity_gross !== undefined && (
                                <>
                                    {(() => {
                                        const isGrossMain = displayMode === 'gross';
                                        const compVal = isGrossMain ? hoverPoint.equity : hoverPoint.equity_gross;
                                        const startVal = data[0]?.equity || 1;
                                        return (
                                            <>
                                                <span className="text-warning-500/60">{isGrossMain ? 'Net' : 'Gross'}</span>
                                                <span className="text-warning-500/80">
                                                    {displayMode === 'percent' ? (compVal >= 0 ? '+' : '') : '$'}
                                                    {Number(displayMode === 'percent'
                                                        ? ((compVal / startVal) - 1) * 100
                                                        : compVal
                                                    ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    {displayMode === 'percent' ? '%' : ''}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                            {allStrategyKeys.filter(k => hoverPoint[k] !== undefined).map((k) => {
                                const idx = allStrategyKeys.indexOf(k);
                                const color = strategySeriesColors[idx % strategySeriesColors.length];
                                return (
                                    <React.Fragment key={k}>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                                            <span className="text-white/40">{k.replace('equity_', '').substring(0, 10)}</span>
                                        </div>
                                        <span className={displayMode === 'percent' ? (hoverPoint[k] >= 0 ? 'text-neon/80' : 'text-error-500/80') : 'text-white/60'}>
                                            {displayMode === 'percent' ? (hoverPoint[k] >= 0 ? '+' : '') : '$'}
                                            {Number(hoverPoint[k] ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            {displayMode === 'percent' ? '%' : ''}
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                            {lowerHoverPoint && (
                                <>
                                    <span className={lowerPaneLabel === 'Daily P/L' && Number(lowerHoverPoint.equity ?? 0) >= 0 ? 'text-success-500' : 'text-error-500'}>{lowerPaneLabel}</span>
                                    <span className={lowerPaneLabel === 'Daily P/L' && Number(lowerHoverPoint.equity ?? 0) >= 0 ? 'text-success-500' : 'text-error-500'}>{lowerPaneValuePrefix}{Number(lowerHoverPoint.equity ?? 0).toFixed(2)}{lowerPaneValueSuffix}</span>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
            {oosStartDate && (
                <div
                    ref={overlayRef}
                    className="absolute top-0 bottom-0 z-5 pointer-events-none border-l border-slate-300/50 bg-slate-300/2.5"
                    style={{ display: 'none' }}
                >
                    <div className="absolute top-2 right-2 rounded-sm bg-slate-200/70 px-1.5 py-0.5 text-[9px] font-bold tracking-widest text-slate-900">
                        OOS
                    </div>
                </div>
            )}
        </div>
    );
};
